<template>
  <div class="fees" :class="{ 'fees--selected': selected }" @click="handleClick">
    <div class="fees__content">
      <div class="fees__cost">
        <span v-if="isUpgrade" class="mr-[2px]">+</span>
        <span class="price__unit">{{ yuan.prefix }}</span>
        <span class="price__number">{{ yuan.value }}</span>
        <span v-if="displayCycle" class="price__cycle"> /{{ displayCycle }} </span>
      </div>
      <div v-if="showPrice.value && !isUpgrade" class="original-price">
        {{ showPrice.prefix }}{{ showPrice.value
        }}<span v-if="displayCycle">/{{ displayCycle }} </span>
      </div>
    </div>
    <div v-if="priceByMonth.value" class="fees__unit">
      <div v-if="fees.buy_type === BUY_TYPE.SUBSCRIBE">
        {{ $t('xu-fei-pricebymonthstring-yue', [priceByMonth.string]) }}
      </div>
      <div v-else-if="!isUpgrade">
        {{
          $t('di-zhi-pricebymonthprefix-pricebymonthvaluetofixed1-yue', [
            priceByMonth.prefix,
            priceByMonth.value.toFixed(1),
          ])
        }}
      </div>
    </div>
    <div class="fees__name">
      {{ packageNameText }}
      <strong v-if="isUpgrade" class="font-normal">
        {{ originalPrice.prefix }}{{ originalPrice.value }}
        <span v-if="displayCycle" class="price__cycle"> /{{ displayCycle }} </span>
      </strong>
    </div>
    <span v-if="isShowTag" class="fees__tag"> {{ $t('li-sheng') }} {{ discount }} </span>
    <span v-if="showHint" class="fees__tag"> {{ hintText }} </span>
  </div>
</template>

<script setup lang="ts">
// 费用卡片：展示单个套餐/订阅价格信息
import { getI18nService } from '@rendora/common/services/i18n';
import { type IFees } from '@rendora/common/store/rights';
import { BUY_TYPE } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import { computed } from 'vue';

const i18nService = getI18nService();
const $t = i18nService.t;

// 入参定义
const props = withDefaults(
  defineProps<{
    fees: IFees;
    isUpgrade?: boolean;
    selected: boolean;
    discount?: string;
  }>(),
  {},
);

// 事件定义
const emits = defineEmits<{
  'on-select': [IFees];
}>();

// 计划类型辅助标识
const isSubscribe = computed(() => props.fees.buy_type === BUY_TYPE.SUBSCRIBE);
const isPayment = computed(() => props.fees.buy_type === BUY_TYPE.PAYMENT);
const isMonthlyPlan = computed(() => props.fees.days === 30);
const isAnnualPlan = computed(() => props.fees.days === 365);

// 界面显示文案
const hintText = computed(() => {
  if (isSubscribe.value) {
    return isMonthlyPlan.value
      ? $t('lian-xu-bao-yue-xiang-you-hui')
      : $t('lian-xu-bao-nian-xiang-you-hui');
  }
  return '';
});
const showHint = computed(() => Boolean(hintText.value));

const displayCycle = computed(() => {
  if (isSubscribe.value) {
    return isMonthlyPlan.value ? $t('yue') : $t('nian');
  }

  return '';
});

const packageNameText = computed(() => {
  if (isPayment.value) {
    if (isMonthlyPlan.value) return $t('yue-tao-can-30-tian');
    if (isAnnualPlan.value) return $t('nian-tao-can-365-tian');
  }
  if (isSubscribe.value) {
    if (isMonthlyPlan.value) return $t('lian-xu-bao-yue');
    if (isAnnualPlan.value) return $t('lian-xu-bao-nian');
  }
  return '';
});

const isShowTag = computed(() => props.fees.days === 365 && props.discount);

const displayPrice = computed(() => {
  return (props.isUpgrade ? props.fees.upgrade_price : props.fees.price) || 0;
});

const yuan = computed(() => formatPrice(displayPrice.value));

const originalPrice = computed(() => formatPrice(props.fees.price || 0));

const priceByMonth = computed(() => {
  const monthsCount = props.fees.months || 1;
  const value = monthsCount > 1 ? displayPrice.value / monthsCount : 0;
  return formatPrice(value);
});

const showPrice = computed(() => formatPrice(props.fees.show_price));

// 点击选择当前费用卡片
function handleClick() {
  emits('on-select', props.fees);
}
</script>

<style lang="scss" scoped>
.fees {
  cursor: pointer;
  width: 150px;
  position: relative;
  height: 100px;
  margin-top: 12px;
  border-radius: var(--x-radius-medium);
  background-color: var(--x-back-0);
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--x-back-4);

  .fees__content {
    display: flex;
    align-items: center;
    padding-left: 16px;
  }

  .original-price {
    font-size: 13px;
    line-height: 18px;
    text-decoration: line-through;
    text-align: center;
    opacity: 0.4;
    margin-left: 8px;
  }

  &--selected {
    border: 1px solid var(--x-purple-6);
    background-color: var(--x-purple-2);
  }
  .fees__name {
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    text-align: left;
    display: block;
    padding-left: 15px;
  }
  &__unit {
    width: 100%;
    line-height: 24px;
    padding-left: 16px;
    font-size: 12px;
    color: var(--x-text-1);
    font-weight: var(--x-weight-medium);
  }
  &__cost {
    text-align: center;
  }
  &__tag {
    font-size: 12px;
    padding: 2px 8px;
    line-height: 18px;
    position: absolute;
    color: var(--x-white-1);
    left: -1px;
    top: -11px;
    font-weight: var(--x-weight-medium);
    background-color: var(--x-error-6);
    border-radius: var(--x-radius-small);
  }

  .price {
    line-height: 1.4;
    margin-bottom: 8px;
    height: 38px;
    &__number {
      font-size: 18px;
      color: var(--x-text-1);
      font-weight: var(--x-weight-medium);
      line-height: 30px;
      margin-bottom: 8px;
    }
    &__unit {
      font-size: 18px;
      color: var(--x-text-1);
      font-weight: var(--x-weight-medium);
    }
  }
}
</style>
